import React from 'react';
import { flexRender } from '@tanstack/react-table';
import {
  TableBody as ShadcnTableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TableLoadingView } from './TableLoadingView';
import { TableErrorView } from './TableErrorView';
import { TableEmptyView } from './TableEmptyView';
import type { TableBodyProps, TableRowData } from '../types';
import { buildColumnSizeStyle, readColumnSize } from '../utils/columnSizing';
import { useDataTableRowExpansion } from '../hooks/useDataTableRowExpansion';

const getVisibleColumnCount = <TRow extends TableRowData>(
  table: TableBodyProps<TRow>['table']
) => table.getVisibleLeafColumns().length;

const getColumnCount = (visibleCount: number, hasExpansion: boolean) =>
  visibleCount + (hasExpansion ? 1 : 0);

const isRowExpanded = (expandedRows: Set<string> | undefined, rowId: string) =>
  expandedRows?.has(rowId) ?? false;

const getContentHeightStyle = (height?: number | string) => {
  if (height === undefined) return undefined;
  return typeof height === 'number' ? `${height}px` : height;
};

const getExpansionContainerStyle = (
  expanded: boolean,
  contentHeight?: string
) =>
  ({
    maxHeight: expanded ? contentHeight : '0px',
    opacity: expanded ? 1 : 0,
    overflow: 'auto',
    pointerEvents: expanded ? 'auto' : 'none',
  }) as const;

const getExpansionContainerClassName = (expanded: boolean) =>
  cn(
    'transition-[max-height,opacity] duration-200 ease-out',
    !expanded && 'overflow-hidden'
  );

const getExpansionCellClassName = (expanded: boolean) =>
  cn('px-0', expanded ? 'py-2' : 'py-0');

// `clipOverflow` is resolved into column meta by useDataTableColumns; absent
// (e.g. a hand-built ColumnDef) means clip.
const shouldClipCell = (meta: unknown): boolean =>
  !(
    typeof meta === 'object' &&
    meta !== null &&
    'clipOverflow' in meta &&
    meta.clipOverflow === false
  );

/**
 * Table body component
 * Renders data rows or placeholder states (loading, error, empty)
 * while keeping headers visible
 */
export function TableBody<TRow extends TableRowData>({
  table,
  isLoading,
  isError,
  error,
  noDataLabel,
  enableRowExpansion = false,
  rowExpansion,
  onRowClick,
  searchValue,
  activeFilterCount,
  expandedRowIds,
  onToggleRowExpansion,
  getRowClassName,
  tableLayout = 'fixed',
}: TableBodyProps<TRow>) {
  const rows = table.getRowModel().rows;
  const visibleColumnCount = getVisibleColumnCount(table);
  const columnCount = getColumnCount(visibleColumnCount, enableRowExpansion);

  const {
    getExpansionId,
    isClickable,
    handleRowClick,
    handleChevronClick,
    highlightedRowIds,
    scrollTargetRowId,
    scrollTargetRef,
    canExpandRow,
  } = useDataTableRowExpansion({
    rows,
    expandedRowIds,
    onToggleRowExpansion,
    onRowClick,
    rowExpansion,
    enableRowExpansion,
  });

  const getErrorMessage = (err: unknown): string =>
    err instanceof Error ? err.message : 'An error occurred';

  if (isLoading) {
    return <TableLoadingView columnCount={columnCount} />;
  }

  if (isError) {
    return (
      <TableErrorView
        error={getErrorMessage(error)}
        columnCount={columnCount}
      />
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <TableEmptyView
        noDataLabel={noDataLabel}
        columnCount={columnCount}
        searchValue={searchValue}
        activeFilterCount={activeFilterCount}
      />
    );
  }

  return (
    <ShadcnTableBody>
      {rows.map(row => {
        const expansionId = getExpansionId(row);
        const expanded = isRowExpanded(expandedRowIds, expansionId);
        const contentHeight = getContentHeightStyle(
          rowExpansion?.contentHeight
        );
        const keepMounted = rowExpansion?.keepMounted ?? true;
        const showContainer = rowExpansion?.showContainer ?? true;

        const customRowClassName = getRowClassName?.(row.original);
        const isHighlighted = highlightedRowIds.has(expansionId);
        const isScrollTarget = expansionId === scrollTargetRowId;
        const expandable = canExpandRow(row);
        const rowIsInteractive =
          isClickable && (expandable || Boolean(onRowClick));

        return (
          <React.Fragment key={row.id}>
            <TableRow
              ref={isScrollTarget ? scrollTargetRef : undefined}
              className={cn(
                'min-h-12 hover:bg-muted/50 !border-b',
                rowIsInteractive && 'cursor-pointer',
                isHighlighted && 'ring-2 ring-blue-500 animate-pulse',
                customRowClassName
              )}
              onClick={rowIsInteractive ? () => handleRowClick(row) : undefined}
            >
              {enableRowExpansion && (
                <TableCell
                  className={cn(
                    'px-2',
                    rowExpansion?.toggleAddon ? 'w-20' : 'w-10'
                  )}
                >
                  <div className="flex items-center gap-1">
                    {expandable && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={event => handleChevronClick(event, row)}
                        aria-label={expanded ? 'Collapse row' : 'Expand row'}
                      >
                        <span
                          className={cn(
                            'inline-flex transition-transform duration-200 ease-out',
                            expanded && 'rotate-90'
                          )}
                        >
                          {rowExpansion?.icon ?? (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                      </Button>
                    )}
                    {rowExpansion?.toggleAddon?.(row.original)}
                  </div>
                </TableCell>
              )}
              {row.getVisibleCells().map(cell => (
                <TableCell
                  key={cell.id}
                  // Stable per-column hook so row-level styling can target a
                  // specific column (e.g. keep the Actions cell bright on a
                  // dimmed suppressed row) without relying on positional
                  // selectors like `:last-child`.
                  data-column-id={cell.column.id}
                  // Clip + ellipsis so a value wider than its column never
                  // paints over the adjacent cell (KIR-4877/4762/4587). Cells
                  // that need the full value on hover wrap it in TruncatedText.
                  // Date and action columns opt out via `clipOverflow: false`.
                  className={cn(
                    'px-3 py-2 h-12',
                    shouldClipCell(cell.column.columnDef.meta) &&
                      'overflow-hidden text-ellipsis'
                  )}
                  style={buildColumnSizeStyle(
                    tableLayout,
                    readColumnSize(cell.column)
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
            {enableRowExpansion &&
              expandable &&
              rowExpansion?.render &&
              keepMounted && (
                <TableRow
                  className={cn(
                    '!border-b',
                    rowExpansion.rowClassName,
                    !expanded && 'hidden'
                  )}
                >
                  <TableCell
                    colSpan={columnCount}
                    className={getExpansionCellClassName(expanded)}
                  >
                    <div
                      className={cn(
                        getExpansionContainerClassName(expanded),
                        showContainer && 'rounded-md border bg-muted/20 p-2',
                        rowExpansion.contentClassName
                      )}
                      style={getExpansionContainerStyle(
                        expanded,
                        contentHeight
                      )}
                    >
                      {expanded && rowExpansion.render(row.original)}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            {enableRowExpansion &&
              expandable &&
              rowExpansion?.render &&
              !keepMounted &&
              expanded && (
                <TableRow
                  className={cn('!border-b', rowExpansion.rowClassName)}
                >
                  <TableCell colSpan={columnCount} className="px-3 py-2">
                    <div
                      className={cn(
                        showContainer && 'rounded-md border bg-muted/20 p-2',
                        rowExpansion.contentClassName
                      )}
                      style={{ maxHeight: contentHeight, overflow: 'auto' }}
                    >
                      {rowExpansion.render(row.original)}
                    </div>
                  </TableCell>
                </TableRow>
              )}
          </React.Fragment>
        );
      })}
    </ShadcnTableBody>
  );
}
